// Radio: one choice from a few, in four shapes that each have a place. RadioGroup names the
// question and holds the answer; inside it sit Radio (a dot and words, for short inline
// choices), RadioRow (a whole row you pick, for choices that need two lines to tell apart),
// RadioSwatch (a colour you pick by its colour) and RadioTile (a picture you pick, in a track).
// A pill strip of words is not here: that is SegTrack. docs/operations/design-standard.md,
// "Controls".
//
// Every shape is a native radio, clipped (sr-only): one Tab stop per group, arrow keys move the
// choice, and a fieldset's `disabled` reaches every option, all from the browser.

import React, { useContext, useState } from 'react';
import { MARK_BOX, markFace, type LabelProps } from './Checkbox';
import { CONTROL_MOTION, PEER_FOCUS, STATE, TOUCH } from './motion';
import Tooltip from './Tooltip';

let seq = 0;
const useUid = (prefix: string) => useState(() => `${prefix}-${++seq}`)[0];

interface GroupContext {
  name: string;
  value: string | null;
  pick: (value: string) => void;
  disabled: boolean;
}

const Group = React.createContext<GroupContext | null>(null);

const LAYOUT = {
  row: 'flex flex-wrap items-center gap-x-4 gap-y-2',
  column: 'flex flex-col gap-1.5',
  swatches: 'flex flex-wrap items-center gap-2',
  track: 'grid gap-0.5 rounded-control bg-surface-container-high p-0.5 shadow-recess',
};

export interface RadioGroupProps<T extends string> {
  /** The question, in words. Always given: a group with no name is a row of unexplained dots. */
  label: React.ReactNode;
  /** Keep the question for a screen reader only, where a heading above already asks it. */
  hideLabel?: boolean;
  value: T | null;
  onChange: (value: T) => void;
  name?: string;
  disabled?: boolean;
  /** `row` for short inline choices, `column` for rows, `swatches` for colours, `track` for tiles. */
  layout?: keyof typeof LAYOUT;
  /** Tiles per line in a `track`; every option on one line when omitted. */
  columns?: number;
  className?: string;
  children: React.ReactNode;
}

export function RadioGroup<T extends string>({
  label,
  hideLabel = false,
  value,
  onChange,
  name,
  disabled = false,
  layout = 'column',
  columns,
  className = '',
  children,
}: RadioGroupProps<T>): React.ReactElement {
  const auto = useUid('ui-radio');
  const legend = `${auto}-legend`;
  const ctx: GroupContext = { name: name || auto, value, pick: (v) => onChange(v as T), disabled };
  const count = columns || React.Children.toArray(children).length || 1;
  return (
    <fieldset role="radiogroup" aria-labelledby={legend} disabled={disabled} className={`m-0 min-w-0 border-0 p-0 ${className}`}>
      <legend id={legend} className={hideLabel ? 'sr-only' : 'mb-2 p-0 text-[13px] font-medium leading-[18px] text-on-surface'}>
        {label}
      </legend>
      <div className={LAYOUT[layout]} style={layout === 'track' ? { gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` } : undefined}>
        <Group.Provider value={ctx}>{children}</Group.Provider>
      </div>
    </fieldset>
  );
}

interface OptionProps {
  value: string;
  disabled?: boolean;
  /** Outside a RadioGroup: the group's name, whether this one is chosen, and what choosing does. */
  name?: string;
  checked?: boolean;
  onChange?: (value: string) => void;
  /** On the wrapping label: a `title` hint, or an `onClick` that stops a click reaching a clickable row. Extra props on the control land on the hidden input, where neither works. */
  labelProps?: LabelProps;
}

const useOption = ({ value, disabled, name, checked, onChange }: OptionProps) => {
  const ctx = useContext(Group);
  return {
    name: ctx ? ctx.name : name,
    on: ctx ? ctx.value === value : !!checked,
    off: !!(disabled || ctx?.disabled),
    pick: () => (ctx ? ctx.pick(value) : onChange?.(value)),
  };
};

/** The drawn dot: the checkbox's well, round, and a dark centre that springs in when chosen. */
const Dot: React.FC<{ on: boolean; disabled: boolean; size: 'sm' | 'md' | 'lg'; className?: string }> = ({ on, disabled, size, className = '' }) => (
  <span
    aria-hidden="true"
    className={`relative inline-flex flex-none items-center justify-center rounded-full motion-safe:group-active/mark:scale-90 ${MARK_BOX[size]} ${markFace(
      on ? 'primary' : null,
      disabled,
      size,
    )} ${CONTROL_MOTION} ${className}`}
  >
    <span
      className={`rounded-full bg-current transition-[transform,opacity] motion-reduce:transition-[opacity] ${size === 'lg' ? 'h-2.5 w-2.5' : 'h-[7px] w-[7px]'} ${
        on ? 'scale-100 opacity-100 duration-spring ease-spring' : 'scale-0 opacity-0 duration-exit ease-exit'
      }`}
    />
  </span>
);

export interface RadioProps extends OptionProps {
  label: React.ReactNode;
  /** A quieter line under the label; spoken as the description. */
  description?: React.ReactNode;
  className?: string;
}

/** A dot and its words: the short inline choice ("Text beside QR", "Two QR per row"). */
export const Radio: React.FC<RadioProps> = ({ label, description, className = '', labelProps, ...option }) => {
  const { name, on, off, pick } = useOption(option);
  const id = useUid('ui-radio-opt');
  return (
    <label {...labelProps} className={`group/mark relative inline-flex min-w-0 max-w-full select-none gap-2 ${description ? 'items-start' : 'items-center'} ${off ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}>
      <input
        type="radio"
        className="peer sr-only"
        name={name}
        value={option.value}
        checked={on}
        disabled={off}
        onChange={pick}
        aria-labelledby={description !== undefined ? `${id}-label` : undefined}
        aria-describedby={description !== undefined ? `${id}-note` : undefined}
      />
      <Dot on={on} disabled={off} size="md" className={`${PEER_FOCUS} ${TOUCH} ${description ? 'mt-px' : ''}`} />
      <span className="min-w-0">
        <span id={`${id}-label`} className={`block text-sm leading-5 ${off ? 'text-on-surface-disabled' : 'text-on-surface'}`}>
          {label}
        </span>
        {description !== undefined && (
          <span id={`${id}-note`} className={`mt-0.5 block text-xs leading-4 ${off ? 'text-on-surface-disabled' : 'text-on-surface-variant'}`}>
            {description}
          </span>
        )}
      </span>
    </label>
  );
};

const ROW_SIZE = {
  md: 'min-h-10 gap-3 px-3 py-2.5 text-sm leading-5',
  // Shop-floor and kiosk screens, worked with gloves or at arm's length.
  lg: 'min-h-14 gap-3 px-4 py-4 text-base leading-6',
};

export interface RadioRowProps extends OptionProps {
  /** What tells this choice apart: a name, a code, a line of facts under it. */
  children: React.ReactNode;
  /** Why it cannot be picked, or a caution to read first. Shown under the row and spoken with it. */
  note?: React.ReactNode;
  /** Draw the dot at the start. Off where the rows are big enough to read as choices on their own. */
  indicator?: boolean;
  size?: 'md' | 'lg';
  className?: string;
}

/**
 * A whole row you pick: the chosen one steps up a tone and glows. For choices that need more
 * than a word (which part to remove, which box to pull from). A list of these that scrolls
 * needs a few pixels of padding, or its edge cuts the glow.
 */
export const RadioRow: React.FC<RadioRowProps> = ({ children, note, indicator = true, size = 'md', className = '', labelProps, ...option }) => {
  const { name, on, off, pick } = useOption(option);
  const noteId = useUid('ui-radio-note');
  const face = off
    ? 'bg-surface-container-low text-on-surface-disabled'
    : on
      ? 'bg-surface-container-highest text-on-surface shadow-glow'
      : `bg-surface-container text-on-surface ${STATE}`;
  return (
    <div className={`min-w-0 ${className}`}>
      <label {...labelProps} className={`group/mark relative block min-w-0 select-none ${off ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <input
          type="radio"
          className="peer sr-only"
          name={name}
          value={option.value}
          checked={on}
          disabled={off}
          onChange={pick}
          aria-describedby={note != null ? noteId : undefined}
        />
        <span
          className={`flex w-full min-w-0 items-start rounded-control text-left ${ROW_SIZE[size]} ${face} ${CONTROL_MOTION} peer-focus-visible:ring-2 peer-focus-visible:ring-primary/70`}
        >
          {indicator && <Dot on={on} disabled={off} size={size === 'lg' ? 'lg' : 'md'} className={size === 'lg' ? '' : 'mt-px'} />}
          <span className="block min-w-0 flex-1">{children}</span>
        </span>
      </label>
      {note != null && (
        <p id={noteId} className="mt-1 px-1 text-xs leading-4 text-amber-300">
          {note}
        </p>
      )}
    </div>
  );
};

export interface RadioSwatchProps extends OptionProps {
  /** The colour itself, as a hex. */
  color: string;
  /** The colour's name: spoken, and shown on hover, so a colour is never the only clue. */
  label: string;
}

/** A colour picked by its colour. Chosen, a ring steps out from it with a gap. */
export const RadioSwatch: React.FC<RadioSwatchProps> = ({ color, label, labelProps, ...option }) => {
  const { name, on, off, pick } = useOption(option);
  return (
    <Tooltip label={label}>
      <label {...labelProps} className={`relative inline-flex flex-none ${off ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <input
          type="radio"
          className="peer sr-only"
          name={name}
          value={option.value}
          checked={on}
          disabled={off}
          onChange={pick}
          aria-label={label}
        />
        <span
          aria-hidden="true"
          style={{ backgroundColor: color }}
          className={`h-6 w-6 rounded-full shadow-[inset_0_0_0_1px_rgb(255_255_255/0.1)] outline outline-2 transition-[outline-color,outline-offset,box-shadow,opacity] duration-quick ease-emphasized peer-focus-visible:ring-2 peer-focus-visible:ring-primary/70 ${off ? 'opacity-[.45]' : STATE} ${TOUCH} ${
            on ? 'outline-offset-2 outline-on-surface' : 'outline-offset-0 outline-transparent'
          }`}
        />
      </label>
    </Tooltip>
  );
};

export interface RadioTileProps extends OptionProps {
  /** The picture: a shape, a face, a template's glyph. */
  children: React.ReactNode;
  /** The words under the picture. Truncates in a narrow tile. */
  caption: string;
  /** Extra classes on the tile face, such as headroom for a picture that rises above it. */
  className?: string;
}

/** A picture picked from a track of them: the chosen tile takes the gold tint, as a segment does. */
export const RadioTile: React.FC<RadioTileProps> = ({ children, caption, className = '', labelProps, ...option }) => {
  const { name, on, off, pick } = useOption(option);
  return (
    <label {...labelProps} className={`group/mark relative flex min-w-0 ${off ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
      <input type="radio" className="peer sr-only" name={name} value={option.value} checked={on} disabled={off} onChange={pick} aria-label={caption} />
      <span
        aria-hidden="true"
        className={`flex w-full min-w-0 flex-col items-center gap-1.5 rounded-inner px-1 pb-2 pt-2 ${CONTROL_MOTION} peer-focus-visible:ring-2 peer-focus-visible:ring-inset peer-focus-visible:ring-primary/70 ${
          off ? 'text-on-surface-disabled' : on ? 'bg-primary/15 text-primary-ink' : `text-on-surface-variant hover:text-on-surface ${STATE}`
        } ${className}`}
      >
        {children}
        <span className="max-w-full truncate text-xs">{caption}</span>
      </span>
    </label>
  );
};

export default RadioGroup;
